import { CloseOutlined, UploadOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import { Button, Flex, Input, Space, Typography, UploadProps } from 'antd';
import { RcFile } from 'antd/es/upload';
import Dragger from 'antd/es/upload/Dragger';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ResourceDocumentList } from '@/components/resource';
import { queryKeys } from '@/lib/react-query';
import { showMessage } from '@/utils';
import { useDeleteResourceDocument } from '@/web-api/resources/useDeleteResourceDocument';
import { useResourceDocuments } from '@/web-api/resources/useResourceDocuments';
import { useUploadResourceDocument } from '@/web-api/resources/useUploadResourceDocument';

type MissingInformationProps = {
  resourceId: string;
};

export const ResourceDocuments = ({ resourceId }: MissingInformationProps) => {
  const [file, setFile] = useState<RcFile | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { Text } = Typography;

  const { data: documents, isLoading: isDocumentsLoading } = useResourceDocuments(resourceId);
  const uploadResourceDocument = useUploadResourceDocument(resourceId);
  const deleteResourceDocument = useDeleteResourceDocument();

  const uploadProps: UploadProps = {
    name: 'file',
    showUploadList: false,
    multiple: false,
    beforeUpload: (file) => {
      setFile(file);
      setFileName(file.name);
      return false;
    },
  };

  function onUploadDocument() {
    if (file) {
      uploadResourceDocument.mutate(
        { file: file, fileName: fileName ?? file.name },
        {
          onSuccess: () => {
            setFile(null);
            void queryClient.invalidateQueries({
              queryKey: queryKeys.resourceDocuments.lists(resourceId ?? ''),
            });
            showMessage('success', t('forms:uploaded'));
          },
        }
      );
    }
  }

  function onDeleteDocument(id: string) {
    deleteResourceDocument.mutate(id, {
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.resourceDocuments.lists(resourceId ?? ''),
        });
        showMessage('success', t('forms:deleted'));
      },
      onError: (error) => {
        console.error('Error deleting document', error);
      },
    });
  }

  return (
    <div>
      <ResourceDocumentList
        documents={documents ?? []}
        isLoading={isDocumentsLoading}
        onDelete={onDeleteDocument}
      />
      <Dragger {...uploadProps}>
        <p className='ant-upload-drag-icon'>
          <UploadOutlined />
        </p>
        {file ? (
          <p className='ant-upload-text'>{file.name}</p>
        ) : (
          <>
            <p className='ant-upload-text'>{t('forms:uploadFileText')}</p>
            <p className='ant-upload-hint'>{t('forms:uploadFileHint')}</p>
          </>
        )}
      </Dragger>
      {file && (
        <Space orientation='vertical' size='small' style={{ marginTop: 16, width: '100%' }}>
          <Text type='secondary'>{t('common:file')}</Text>
          <Input
            value={fileName ?? ''}
            onChange={(e) => setFileName(e.target.value)}
            placeholder={t('common:file')}
          />
        </Space>
      )}
      <Flex justify='space-between' align='center' style={{ marginTop: 16 }}>
        <Button
          type='default'
          hidden={!file}
          onClick={() => setFile(null)}
          disabled={uploadResourceDocument.isPending}
          icon={<CloseOutlined />}
        >
          {t('forms:discard')}
        </Button>
        <Button
          hidden={!file}
          type='primary'
          loading={uploadResourceDocument.isPending}
          onClick={onUploadDocument}
        >
          {t('forms:upload')}
        </Button>
      </Flex>
    </div>
  );
};
